import * as React from 'react';
import { Icon, Typography } from '../common';
import Icons from '@/const/icons';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CaseApplications, CaseDetailsLawyer } from '@/interfaces';
import { formatStates } from '@/lib';
import { formatDateToUS } from '@/utils';
import { BadgeTypes, useClaimIcons } from '@/hooks';
import clsx from 'clsx';

interface CaseOverviewModalProps {
	caseApplications: CaseApplications | CaseDetailsLawyer;
	hideConfirmAccount: () => void;
}

export const CaseOverviewModal: React.FC<CaseOverviewModalProps> = ({
	caseApplications,
	hideConfirmAccount,
}) => {
	const { getIcon } = useClaimIcons();
	const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
	const formattedType = (): string => {
		if ('category_name' in caseApplications) {
			return caseApplications.category_name.toLowerCase().replace(' ', '_');
		} else if ('category' in caseApplications) {
			return (
				caseApplications.category?.name.toLowerCase()?.replace(' ', '_') || ''
			);
		}
		return '';
	};

	const { statesToList, statesToModal } = formatStates(caseApplications.states);
	const statesLength = statesToList.split(',').length;

	return (
		<div className="lg:px-4 lg:w-[933px]">
			<div className="relative flex items-center w-full justify-center mt-8 lg:mt-4 mb-6 gap-x-2 lg:gap-x-8">
				<div className="flex items-center gap-x-1 lg:gap-x-2">
					{getIcon(formattedType() as BadgeTypes, '!text-purple-150')}
					<Typography type="semibold-12">
						{'category_name' in caseApplications
							? caseApplications.category_name
							: caseApplications.category?.name}
					</Typography>
				</div>
				<div className="bg-primary h-4 w-0.5" />
				<div className="flex items-center gap-x-1 lg:gap-x-2 max-w-96">
					<span className="w-4 h-4 flex-shrink-0">
						<Icon src={Icons.location} className="w-4 h-4 text-purple-150" />
					</span>
					<Typography type="semibold-12">{statesToList}</Typography>
					{/* Changed the <TooltipComponent/> to this because it was rendering under the modal */}
					{statesToModal.length > 0 && (
						<div
							onMouseEnter={() => setIsTooltipOpen(true)}
							onMouseLeave={() => setIsTooltipOpen(false)}
							className="relative text-start"
						>
							<Typography
								type="semibold-12"
								className="text-purple-150 cursor-pointer"
							>
								{statesToModal.length > 4
									? `(${statesToModal.length - statesLength}+)`
									: ''}
							</Typography>

							{isTooltipOpen && (
								<div className="absolute z-50 top-4 left-4 mt-2 w-[283px] py-4 px-8 justify-start rounded-[20px] bg-white shadow-lg">
									<Typography type="regular-16">Eligible States</Typography>
									<Typography type="regular-16" className="opacity-60">
										{statesToModal.join(', ')}.
									</Typography>
								</div>
							)}
						</div>
					)}
				</div>
				<button type="button" onClick={hideConfirmAccount}>
					<XMarkIcon className="text-black w-8 h-8 absolute right-0 -top-8 lg:-top-1" />
				</button>
			</div>
			<Typography
				type="headline-3"
				className="text-center mb-6 lg:mb-14 !text-2xl lg:!text-3xl"
			>
				{caseApplications.title}
			</Typography>
			<div className="max-h-[500px] lg:max-h-full overflow-y-auto scroll-custom text-left">
				<Typography type="semibold-20" className="text-primary mb-4">
					About the company:
				</Typography>
				<div
					dangerouslySetInnerHTML={{
						__html: caseApplications.about_company ?? '',
					}}
					className={clsx(
						'text-sm lg:text-lg text-opacity-black mb-12 whitespace-pre-wrap break-words !font-sans'
					)}
				/>
				<Typography type="semibold-20" className="text-primary mb-4">
					About the claim:
				</Typography>
				<div
					dangerouslySetInnerHTML={{
						__html: caseApplications.about_claim ?? '',
					}}
					className={clsx(
						'text-sm lg:text-lg text-opacity-black mb-12 whitespace-pre-wrap break-words !font-sans'
					)}
				/>
				<Typography type="semibold-20" className="text-primary mb-4">
					Eligibility criteria:
				</Typography>
				<div
					dangerouslySetInnerHTML={{
						__html: caseApplications.eligibility_criteria ?? '',
					}}
					className={clsx(
						'text-sm lg:text-lg text-opacity-black mb-12 whitespace-pre-wrap break-words !font-sans'
					)}
				/>
				{caseApplications.registration_deadline && (
					<>
						<Typography type="semibold-20" className="text-primary mb-4">
							Registration deadline:
						</Typography>
						<Typography type="regular-18" className="text-opacity-black mb-12">
							{formatDateToUS(caseApplications.registration_deadline)}
						</Typography>
					</>
				)}
			</div>
		</div>
	);
};
